import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CookieService } from '../../core/services/cookie.service';
import { BreadcrumbService } from '../../shared/components/breadcrumb/breadcrumb.service';
import { User } from '../../shared/models/user.interface';

export interface SignInPayload {
  email: string;
  password: string;
}

export interface SignInResponse {
  message?: string;
}

export interface VerifyOtpPayload {
  email: string;
  otp: number;
}

export interface VerifyOtpResponse {
  accessToken: string;
  message: string;
  refreshToken: string;
  user?: User;
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordPayload {
  email: string;
  otp: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly API_URL = environment.apiUrl;
  private currentEmail: string = '';
  private verifiedResetOtp: string = '';
  private readonly REMEMBERED_EMAIL_KEY = 'remembered_email';
  private readonly REMEMBERED_PASSWORD_KEY = 'remembered_password';

  signIn(payload: SignInPayload): Observable<SignInResponse> {
    return this.http.post<SignInResponse>(`${this.API_URL}/auth/login`, payload)
      .pipe(
        tap(response => {
          this.currentEmail = payload.email;
        })
      );
  }

  verifyOtp(payload: VerifyOtpPayload): Observable<VerifyOtpResponse> {
    return this.http.post<VerifyOtpResponse>(`${this.API_URL}/auth/verify-otp`, payload)
      .pipe(
        tap(response => {
          if (response) {
            this.currentEmail = '';
          }
        })
      );
  }

  resendOtp(payload: { email: string, flow:string }): Observable<any> {
    return this.http.post(`${this.API_URL}/auth/resend-otp`, payload);
  }

  // Forgot Password Flow
  forgotPassword(payload: ForgotPasswordPayload): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(`${this.API_URL}/auth/forgot-password`, payload)
      .pipe(
        tap(response => {
          this.currentEmail = payload.email;
        })
      );
  }

  verifyResetOtp(payload: VerifyOtpPayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/verify-reset-otp`, payload)
      .pipe(
        tap(response => {
          this.verifiedResetOtp = payload.otp.toString();
        })
      );
  }

  resetPassword(payload: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(`${this.API_URL}/auth/reset-password`, payload)
      .pipe(
        tap(response => {
          this.currentEmail = '';
          this.verifiedResetOtp = '';
        })
      );
  }

  getCurrentEmail(): string {
    return this.currentEmail;
  }

  setCurrentEmail(email: string): void {
    this.currentEmail = email;
  }

  getVerifiedResetOtp(): string {
    return this.verifiedResetOtp;
  }

  // Token management helpers
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    this.currentEmail = '';
    this.verifiedResetOtp = '';
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  // User management helpers
  getCurrentUser(): User | null {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  }

  setCurrentUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  clearCurrentUser(): void {
    localStorage.removeItem('user');
  }

  // Role-based helpers
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.is_admin ?? false;
  }

  isCustomer(): boolean {
    const user = this.getCurrentUser();
    return user ? !user.is_admin : false;
  }

  getUserRole(): 'admin' | 'customer' | null {
    const user = this.getCurrentUser();
    if (!user) return null;
    return user.is_admin ? 'admin' : 'customer';
  }

  // Remember me functionality
  saveCredentials(email: string, password: string): void {
    // Encode credentials for basic security (not production-ready encryption)
    const encodedEmail = btoa(email);
    const encodedPassword = btoa(password);
    
    this.cookieService.setCookie(this.REMEMBERED_EMAIL_KEY, encodedEmail, 30); // 30 days
    this.cookieService.setCookie(this.REMEMBERED_PASSWORD_KEY, encodedPassword, 30);
  }

  getRememberedCredentials(): { email: string; password: string } | null {
    const encodedEmail = this.cookieService.getCookie(this.REMEMBERED_EMAIL_KEY);
    const encodedPassword = this.cookieService.getCookie(this.REMEMBERED_PASSWORD_KEY);

    if (encodedEmail && encodedPassword) {
      try {
        return {
          email: atob(encodedEmail),
          password: atob(encodedPassword)
        };
      } catch (error) {
        // If decoding fails, clear invalid cookies
        this.clearRememberedCredentials();
        return null;
      }
    }
    return null;
  }

  clearRememberedCredentials(): void {
    this.cookieService.deleteCookie(this.REMEMBERED_EMAIL_KEY);
    this.cookieService.deleteCookie(this.REMEMBERED_PASSWORD_KEY);
  }

  hasRememberedCredentials(): boolean {
    return this.cookieService.hasCookie(this.REMEMBERED_EMAIL_KEY) && 
           this.cookieService.hasCookie(this.REMEMBERED_PASSWORD_KEY);
  }

  // Refresh token functionality
  refreshToken(): Observable<VerifyOtpResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.warn('AuthService: No refresh token available for token refresh');
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<VerifyOtpResponse>(`${this.API_URL}/auth/refresh`, {
      refreshToken
    }).pipe(
      tap(response => {
        // Validate response structure
        if (!response?.accessToken) {
          throw new Error('Invalid refresh response: missing accessToken');
        }
        
        // Update tokens in localStorage
        localStorage.setItem('accessToken', response.accessToken);
        
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        
        if (response.user) {
          this.setCurrentUser(response.user);
        }
      }),
      catchError((error: any) => {
        console.error('AuthService: Token refresh failed', error);
        
        // Clear invalid tokens
        // this.clearTokens();
        
        // Re-throw with more context
        return throwError(() => error);
      })
    );
  }

  // Logout functionality
  logout(): void {
    this.clearTokens();
    this.clearCurrentUser();
    this.breadcrumbService.clear();
    this.currentEmail = '';
    this.verifiedResetOtp = '';
    // Note: We don't clear remembered credentials on logout
    // so users can still use "Remember Me" functionality
  }
}
